import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useAcceptInvite } from '../hooks/useMembers'
import { supabase } from '../lib/supabase'

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>()
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const accept = useAcceptInvite()

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  // Se non autenticato, redirect a login con next param
  useEffect(() => {
    if (!authLoading && !user) {
      navigate(`/login?next=/invito/${token}`, { replace: true })
    }
  }, [user, authLoading, token, navigate])

  async function handleAccept() {
    if (!token) return
    setStatus('loading')
    try {
      await accept.mutateAsync(token)
      // Force JWT refresh per aggiornare app_metadata.tenant_id
      await supabase.auth.refreshSession()
      setStatus('success')
      setTimeout(() => navigate('/'), 2000)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore durante l\'accettazione'
      setErrorMsg(msg)
      setStatus('error')
    }
  }

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 size={28} className="animate-spin text-indigo-400" />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[--km-bg] px-4">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[--km-surface] p-8 shadow-xl text-center flex flex-col items-center gap-5">
        {status === 'success' && (
          <>
            <CheckCircle2 size={48} className="text-emerald-400" />
            <h1 className="text-xl font-bold text-[--km-text-primary]">Benvenuto nel team!</h1>
            <p className="text-sm text-[--km-text-muted]">Accesso attivato. Stai per essere reindirizzato alla dashboard…</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle size={48} className="text-red-400" />
            <h1 className="text-xl font-bold text-[--km-text-primary]">Link non valido</h1>
            <p className="text-sm text-[--km-error]">{errorMsg}</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-2 px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors"
            >
              Torna al login
            </button>
          </>
        )}

        {(status === 'idle' || status === 'loading') && (
          <>
            <div className="p-4 rounded-2xl bg-indigo-500/10 border border-indigo-500/20">
              <CheckCircle2 size={32} className="text-indigo-400" />
            </div>
            <h1 className="text-xl font-bold text-[--km-text-primary]">Sei stato invitato</h1>
            <p className="text-sm text-[--km-text-muted]">
              Stai per accedere come collaboratore. Clicca il pulsante per accettare l&apos;invito.
            </p>
            <button
              onClick={handleAccept}
              disabled={status === 'loading'}
              className="mt-2 w-full flex items-center justify-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-medium py-3 transition-colors"
            >
              {status === 'loading' ? <Loader2 size={18} className="animate-spin" /> : null}
              {status === 'loading' ? 'Attivazione in corso…' : 'Accetta invito'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
